import axios from 'axios';

const API = `${window.location.origin}/api`;

// Create axios instance with auth header
const api = axios.create({
    baseURL: API,
    withCredentials: true
});

// Add credentials and auth header to all requests
api.interceptors.request.use((config) => {
    config.withCredentials = true;
    const token = sessionStorage.getItem('access_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle 401 errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            sessionStorage.removeItem('access_token');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Restaurant APIs
export const getRestaurants = () => api.get('/restaurants');
export const getRestaurant = (id) => api.get(`/restaurants/${id}`);
export const createRestaurant = (data) => api.post('/restaurants', data);
export const updateRestaurant = (id, data) => api.put(`/restaurants/${id}`, data);
export const deleteRestaurant = (id) => api.delete(`/restaurants/${id}`);
export const duplicateRestaurant = (id) => api.post(`/restaurants/${id}/duplicate`);

// Category APIs
export const getCategories = (restaurantId) => api.get(`/restaurants/${restaurantId}/categories`);
export const createCategory = (restaurantId, data) => api.post(`/restaurants/${restaurantId}/categories`, data);
export const updateCategory = (id, data) => api.put(`/categories/${id}`, data);
export const deleteCategory = (id) => api.delete(`/categories/${id}`);

// Dish APIs
export const getDishes = (restaurantId) => api.get(`/restaurants/${restaurantId}/dishes`);
export const createDish = (restaurantId, data) => api.post(`/restaurants/${restaurantId}/dishes`, data);
export const updateDish = (id, data) => api.put(`/dishes/${id}`, data);
export const deleteDish = (id) => api.delete(`/dishes/${id}`);

// Dish Label APIs
export const getDishLabels = (dishId) => api.get(`/dishes/${dishId}/labels`);
export const createDishLabel = (dishId, data) => api.post(`/dishes/${dishId}/labels`, data);
export const updateDishLabel = (id, data) => api.put(`/labels/${id}`, data);
export const deleteDishLabel = (id) => api.delete(`/labels/${id}`);
export const bulkUpdateDishLabels = (dishId, labels) => api.put(`/dishes/${dishId}/labels/bulk`, labels);

// File Upload APIs
export const uploadImage = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
};

export const uploadModel = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/model', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
};

export const uploadVideo = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/video', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
    });
};

export const getFileUrl = (path) => `${API}/files/${path}`;

// PDF Export - uses httpOnly cookie auth
export const getMenuPdfUrl = (restaurantId) => {
    return `${API}/restaurants/${restaurantId}/pdf`;
};
export const downloadMenuPdf = (restaurantId) => api.get(`/restaurants/${restaurantId}/pdf`, { responseType: 'blob' });

// Analytics API
export const getAnalytics = () => api.get('/analytics');

// Shop Admin APIs (Super Admin only)
export const getShopAdmins = () => api.get('/shop-admins');
export const createShopAdmin = (data) => api.post('/shop-admins', data);
export const updateShopAdmin = (id, data) => api.put(`/shop-admins/${id}`, data);
export const deleteShopAdmin = (id) => api.delete(`/shop-admins/${id}`);

// Live Orders APIs
export const getOrders = (restaurantId, status = '') => api.get(`/orders/${restaurantId}${status ? `?status=${status}` : ''}`);
export const updateOrderStatus = (restaurantId, orderId, status) => api.put(`/orders/${restaurantId}/${orderId}/status`, { status });
export const generateOrderKey = (restaurantId) => api.post(`/restaurants/${restaurantId}/generate-order-key`);

// i18n
export const getTranslations = (lang) => axios.get(`${API}/translations/${lang}`);
export const getLanguages = () => axios.get(`${API}/translations`);

// Public Menu API (no auth needed)
export const getPublicMenu = (slug) => axios.get(`${API}/menu/${slug}`);
export const getPublicDish = (slug, dishId) => axios.get(`${API}/menu/${slug}/dish/${dishId}`);

// Dashboard Stats
export const getDashboardStats = () => api.get('/dashboard-stats');

// Order Tracking (public)
export const trackOrder = (orderId) => axios.get(`${API}/track/${orderId}`);

// Theme Presets
export const getThemePresets = () => api.get('/theme-presets');

// Bulk CSV Import
export const importDishesCSV = (restaurantId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/restaurants/${restaurantId}/import-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
};

// Reorder
export const reorderCategories = (restaurantId, order) => api.put(`/restaurants/${restaurantId}/reorder-categories`, order);
export const reorderDishes = (restaurantId, order) => api.put(`/restaurants/${restaurantId}/reorder-dishes`, order);

// ============ PERFUME API ============
export const getPerfumeShops = () => api.get('/perfume-shops');
export const createPerfumeShop = (data) => api.post('/perfume-shops', data);
export const getPerfumeShop = (id) => api.get(`/perfume-shops/${id}`);
export const updatePerfumeShop = (id, data) => api.put(`/perfume-shops/${id}`, data);
export const deletePerfumeShop = (id) => api.delete(`/perfume-shops/${id}`);
export const getPerfumes = (shopId, collection) => api.get(`/perfume-shops/${shopId}/perfumes${collection ? `?collection=${collection}` : ''}`);
export const createPerfume = (shopId, data) => api.post(`/perfume-shops/${shopId}/perfumes`, data);
export const getPerfume = (id) => api.get(`/perfumes/${id}`);
export const updatePerfume = (id, data) => api.put(`/perfumes/${id}`, data);
export const deletePerfume = (id) => api.delete(`/perfumes/${id}`);
export const reorderPerfumes = (shopId, order) => api.put(`/perfume-shops/${shopId}/reorder-perfumes`, order);
export const setTop5 = (shopId, ids) => api.put(`/perfume-shops/${shopId}/top5`, ids);
export const getPerfumeCollections = (shopId) => api.get(`/perfume-shops/${shopId}/collections`);

// Fragrantica Scraper
export const scrapeFragrantica = (url) => api.post('/scrape-fragrantica', { url });

export default api;
